<template>
    <el-dialog
        :visible="visible"
        :modal-append-to-body="false"
        :close-on-click-modal="false"
        append-to-body
        class="outsourcing-vendor-import-dialog ah-detail-dialog"
        :width="parsedRows.length > 0 ? null : '400px'"
        top="10vh"
        :before-close="() => onClose()"
        :show-close="false"
        @open="onOpen"
    >
        <template slot="title">批次匯入委外廠商</template>

        <div class="detail-row">
            <!-- 上傳區 -->
            <div v-if="parsedRows.length === 0" class="upload-area">
                <el-button type="primary" class="upload-btn" @click="$refs.fileInput.click()">上傳 Excel 檔案</el-button>
                <div class="hint">請使用下方「下載匯入範例」所提供的標準格式。</div>
                <div>
                    <el-button @click="onDownloadTemplate">下載匯入範例</el-button>
                    <el-button plain @click="onClose()">取消</el-button>
                </div>
            </div>

            <!-- 預覽表格 -->
            <template v-else>
                <el-alert v-if="warningCount > 0" type="warning" :closable="false" style="margin-bottom: 12px">
                    共 {{ warningCount }} 筆資料有預檢警告，匯入時可能失敗
                </el-alert>
                <el-table :data="parsedRows" border style="width: 100%" height="300px" empty-text="沒有資料">
                    <el-table-column type="index" label="#" width="50" fixed />
                    <el-table-column label="匯入狀態" width="120" fixed>
                        <template slot-scope="{ row }">
                            <span :class="statusClass(row.importStatus)">{{ statusLabel(row.importStatus) }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="預檢" width="140" show-overflow-tooltip>
                        <template slot-scope="{ row }">
                            <span v-if="row._warning" style="color: #E6A23C">{{ row._warning }}</span>
                            <span v-else style="color: #67C23A">OK</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="公司名稱" prop="_fnam" width="160" show-overflow-tooltip />
                    <el-table-column label="類型" prop="_catName" width="120" />
                    <el-table-column label="統一編號" prop="_bsn" width="120" />
                    <el-table-column label="聯絡窗口數" prop="_contactCount" width="100" />
                    <el-table-column label="帳戶數" prop="_bankCount" width="80" />
                </el-table>
            </template>
        </div>

        <!-- 底部按鈕 -->
        <div class="detail-row footer right" v-if="parsedRows.length > 0">
            <div style="display: flex; justify-content: space-between; align-items: center;">
                <template v-if="importStatus === 'init'">
                    <div>
                        <el-button plain @click="onReset">重新上傳</el-button>
                        <el-button plain @click="onClose()">取消</el-button>
                    </div>
                    <div>
                        <el-button type="primary" @click="onImport">開始匯入 {{ parsedRows.length }} 筆資料</el-button>
                    </div>
                </template>
                <template v-else>
                    <div style="flex: 0.9">
                        <el-progress :percentage="progress" :color="progress === 100 ? '#92DE56' : '#463cb2'" />
                    </div>
                    <div v-if="importStatus === 'importing'">
                        <el-button :loading="isStopping" plain @click="isStopping = true">
                            {{ isStopping ? '停止中' : '停止' }}
                        </el-button>
                    </div>
                    <div v-else>
                        <el-button type="primary" @click="onClose(true)">完成</el-button>
                    </div>
                </template>
            </div>
        </div>

        <input v-show="false" ref="fileInput" type="file" accept=".xlsx" @change="onFileChange" />
    </el-dialog>
</template>

<script>
import XLSX from 'xlsx';
import ExcelJS from 'exceljs';

const MAX_CONTACTS = 3;
const MAX_BANKS = 3;

// 共用欄位佈局定義（匯出 / 範例 Excel 共用格式）
function buildColumnLayout() {
    const columns = [
        { header: '系統編號',       key: 'voo',          width: 14, section: 'basic', hint: '新增時不需填寫' },
        { header: '類型',           key: 'caT_NAM',      width: 20, section: 'basic', required: true, hint: '必須在「委外類型」中已建立' },
        { header: '公司名稱',       key: 'fnam',         width: 40, section: 'basic', required: true },
        { header: '統一編號',       key: 'bsn',          width: 14, section: 'basic', text: true, hint: '營利事業必填' },
        { header: '國籍',           key: '_abd',         width: 12, section: 'basic', required: true, hint: '本國/境外' },
        { header: '境外類型',       key: '_abD_TYP',     width: 14, section: 'basic', hint: '境外必填\n政府/公營事業/民間' },
        { header: '營利類型',       key: '_profitType',   width: 16, section: 'basic', required: true, hint: '營利事業/非營利事業' },
        { header: '縣市／國家',     key: '_cityOrArea',   width: 16, section: 'basic' },
        { header: '地址',           key: 'adr',          width: 40, section: 'basic' },
        { header: '郵遞區號',       key: 'adR_CODE',     width: 10, section: 'basic', text: true },
        { header: '備註',           key: 'note',         width: 40, section: 'basic' },
    ];
    for (let n = 1; n <= MAX_CONTACTS; n++) {
        columns.push(
            { header: `聯絡窗口${n}_姓名`,  key: `c${n}_nam`, width: 16, section: 'contact' },
            { header: `聯絡窗口${n}_稱謂`,  key: `c${n}_gen`, width: 12, section: 'contact', hint: '先生/小姐' },
            { header: `聯絡窗口${n}_職稱`,  key: `c${n}_job`, width: 16, section: 'contact' },
            { header: `聯絡窗口${n}_電話`,  key: `c${n}_tel`, width: 16, section: 'contact', text: true },
            { header: `聯絡窗口${n}_分機`,  key: `c${n}_ext`, width: 10, section: 'contact', text: true },
            { header: `聯絡窗口${n}_Email`, key: `c${n}_eml`, width: 26, section: 'contact' },
        );
    }
    for (let n = 1; n <= MAX_BANKS; n++) {
        columns.push(
            { header: `帳戶${n}_類型`,         key: `b${n}_type`,   width: 14, section: 'bank', hint: '本國帳戶/外國帳戶' },
            { header: `帳戶${n}_戶名`,         key: `b${n}_tpc`,    width: 20, section: 'bank' },
            { header: `帳戶${n}_銀行代號`,     key: `b${n}_boo`,    width: 14, section: 'bank', text: true, hint: '本國帳戶用，共3碼' },
            { header: `帳戶${n}_分行代號`,     key: `b${n}_doo`,    width: 14, section: 'bank', text: true, hint: '本國帳戶用，共7碼' },
            { header: `帳戶${n}_收款帳號`,     key: `b${n}_acc`,    width: 22, section: 'bank', text: true },
            { header: `帳戶${n}_銀行英文名稱`, key: `b${n}_abroad`, width: 24, section: 'bank', hint: '外國帳戶必填' },
            { header: `帳戶${n}_SWIFT Code`,   key: `b${n}_swift`,  width: 16, section: 'bank', hint: '外國帳戶必填' },
            { header: `帳戶${n}_備註`,         key: `b${n}_note`,   width: 20, section: 'bank' },
        );
    }
    return columns;
}

const SECTION_COLORS = { basic: 'FFE2EFDA', contact: 'FFDAE8FC', bank: 'FFFFF2CC' };

function buildHeaderText(col) {
    let text = col.required ? `*${col.header}` : col.header;
    if (col.hint) text += `\r\n(${col.hint})`;
    return text;
}

function normalizeKey(key) {
    return key.replace(/^\*|\*$/g, '').split(/\r?\n/)[0].trim();
}

export default {
    name: 'OutsourcingVendorImportDialog',
    props: {
        visible: Boolean,
    },
    data() {
        return {
            parsedRows: [],
            importStatus: 'init',
            isStopping: false,
            progress: 0,
            options: [],
        };
    },
    computed: {
        warningCount() {
            return this.parsedRows.filter(r => r._warning).length;
        },
    },
    methods: {
        onOpen() {
            this.parsedRows = [];
            this.importStatus = 'init';
            this.isStopping = false;
            this.progress = 0;
            this.fetchOptions();
        },
        onReset() {
            this.parsedRows = [];
            this.importStatus = 'init';
            this.progress = 0;
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        },
        onClose(refresh) {
            this.$emit('close', refresh);
        },
        async fetchOptions() {
            const resp = await this.$api({ url: '/Tool/GetDepartmentAndVendorSettings', method: 'GET' });
            this.options = resp.data.vendorSettings_VD;
        },

        // ── 下載匯入範例 ──────────────────────────────────────────
        async onDownloadTemplate() {
            const columns = buildColumnLayout();
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('import');
            worksheet.columns = columns.map(col => ({ header: col.header, key: col.key, width: col.width }));

            // 覆寫標題列：加上 * 必填前綴和換行註解
            const headerRow = worksheet.getRow(1);
            columns.forEach((col, idx) => {
                const cell = headerRow.getCell(idx + 1);
                cell.value = buildHeaderText(col);
                cell.font = { bold: true, size: 10 };
                cell.alignment = { wrapText: true, vertical: 'top' };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SECTION_COLORS[col.section] } };
            });
            headerRow.height = 48;

            // 範例資料（2 筆：本國營利 + 境外）
            worksheet.addRow({
                voo: '', caT_NAM: '音樂製作', fnam: '示範國內股份有限公司', bsn: '12345678',
                _abd: '本國', _abD_TYP: '', _profitType: '營利事業', _cityOrArea: '臺北市',
                adr: '中正區林森北路一段48號', adR_CODE: '105', note: '',
                c1_nam: '測試廠商', c1_gen: '先生', c1_job: '', c1_tel: '0212344321', c1_ext: '777', c1_eml: 'test@mail.com',
                b1_type: '本國帳戶', b1_tpc: '示範國內股份有限公司', b1_boo: '004', b1_doo: '0040037', b1_acc: '1234567890', b1_abroad: '', b1_swift: '', b1_note: '',
            });
            worksheet.addRow({
                voo: '', caT_NAM: '境外', fnam: '示範外國公司', bsn: '87654321',
                _abd: '境外', _abD_TYP: '民間', _profitType: '營利事業', _cityOrArea: '美國',
                adr: '45 Rockefeller Plaza, New York, NY 10111, U.S.', adR_CODE: '10111', note: '預計10/31開始外包',
                c1_nam: '國外廠商', c1_gen: '小姐', c1_job: '', c1_tel: '+12125888601', c1_ext: '1', c1_eml: 'test@mail.com',
                b1_type: '外國帳戶', b1_tpc: '示範外國公司', b1_boo: '', b1_doo: '', b1_acc: '111111111', b1_abroad: 'Bank of America', b1_swift: '654321', b1_note: '',
            });

            // 資料列文字格式（防數字轉換）
            const textCols = columns.reduce((acc, col, idx) => { if (col.text) acc.push(idx + 1); return acc; }, []);
            worksheet.eachRow((row, rowNum) => {
                if (rowNum === 1) return;
                textCols.forEach(colNum => {
                    const cell = row.getCell(colNum);
                    if (cell.value != null && cell.value !== '') cell.numFmt = '@';
                });
            });

            worksheet.views = [{ state: 'frozen', ySplit: 1 }];

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = '委外廠商匯入範例_新版.xlsx';
            a.click();
            URL.revokeObjectURL(url);
        },

        // ── 檔案解析 ──────────────────────────────────────────────
        onFileChange(event) {
            const file = event.target.files[0];
            if (!file) return;
            event.target.value = '';

            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const wb = XLSX.read(e.target.result, { type: 'array' });
                    if (!wb.Sheets.import) throw new Error('找不到工作表「import」，請確認檔案為委外廠商匯出的 Excel');

                    const rawSheet = XLSX.utils.sheet_to_json(wb.Sheets.import, { defval: '' });
                    if (rawSheet.length === 0) throw new Error('Excel 內沒有資料列');

                    // 正規化 key：去除 * 前後綴，取 \r\n 前的第一行
                    const raw = rawSheet.map(row => {
                        const cleaned = {};
                        Object.keys(row).forEach(k => { cleaned[normalizeKey(k)] = row[k]; });
                        return cleaned;
                    });

                    this.parsedRows = raw.map((row) => {
                        const contacts = this.parseContacts(row);
                        const banks = this.parseBanks(row);
                        const fnam = row['公司名稱'] || '';
                        const catName = row['類型'] || '';
                        const warning = this.preValidate(row, fnam, catName);

                        return {
                            _raw: row,
                            _fnam: fnam,
                            _catName: catName,
                            _bsn: row['統一編號'] ? String(row['統一編號']) : '',
                            _contactCount: contacts.length,
                            _bankCount: banks.length,
                            _warning: warning,
                            importStatus: 'init',
                        };
                    });
                } catch (err) {
                    this.$message({ showClose: true, message: err.message || '檔案解析失敗', type: 'error' });
                }
            };
            reader.readAsArrayBuffer(file);
        },

        // ── 預檢驗證（不阻擋匯入，僅顯示警告）──────────────────
        preValidate(row, fnam, catName) {
            const warnings = [];
            if (!fnam) warnings.push('缺公司名稱');
            if (!catName) warnings.push('缺類型');
            if (catName && !this.options.find(o => o.catnam === catName)) warnings.push('類型無效');
            return warnings.length > 0 ? warnings.join('、') : '';
        },

        // ── 解析聯絡窗口 ──────────────────────────────────────────
        parseContacts(row) {
            const contacts = [];
            for (let i = 1; i <= MAX_CONTACTS; i++) {
                const nam = row[`聯絡窗口${i}_姓名`];
                if (!nam) continue;
                contacts.push({
                    nam,
                    gen: row[`聯絡窗口${i}_稱謂`] === '小姐' ? 1 : 0,
                    job: row[`聯絡窗口${i}_職稱`] || '',
                    tel: row[`聯絡窗口${i}_電話`] ? String(row[`聯絡窗口${i}_電話`]) : '',
                    ext: row[`聯絡窗口${i}_分機`] ? String(row[`聯絡窗口${i}_分機`]) : '',
                    eml: row[`聯絡窗口${i}_Email`] || '',
                    dep: '',
                    onf: 1,
                });
            }
            return contacts;
        },

        // ── 解析收款帳戶 ──────────────────────────────────────────
        parseBanks(row) {
            const banks = [];
            for (let i = 1; i <= MAX_BANKS; i++) {
                const tpc = row[`帳戶${i}_戶名`];
                const acc = row[`帳戶${i}_收款帳號`];
                if (!tpc && !acc) continue;
                const accountType = row[`帳戶${i}_類型`] === '外國帳戶' ? 2 : 1;
                const bank = {
                    accountType,
                    bnK_TPC: tpc || '',
                    bnK_ACC: acc ? String(acc) : '',
                    bnK_BOO: null,
                    bnK_DOO: null,
                    abroadBankName: null,
                    swiftCode: null,
                    note: row[`帳戶${i}_備註`] || '',
                };
                if (accountType === 1) {
                    bank.bnK_BOO = row[`帳戶${i}_銀行代號`] ? String(row[`帳戶${i}_銀行代號`]) : null;
                    bank.bnK_DOO = row[`帳戶${i}_分行代號`] ? String(row[`帳戶${i}_分行代號`]) : null;
                } else {
                    bank.abroadBankName = row[`帳戶${i}_銀行英文名稱`] || '';
                    bank.swiftCode = row[`帳戶${i}_SWIFT Code`] ? String(row[`帳戶${i}_SWIFT Code`]) : '';
                }
                banks.push(bank);
            }
            return banks;
        },

        // ── 組裝 API payload ──────────────────────────────────────
        buildPayload(raw) {
            const category = this.options.find(o => o.catnam === raw['類型']);
            if (!category) throw new Error(`無效的委外廠商類型：「${raw['類型']}」`);

            const fnam = raw['公司名稱'] || '';
            if (!fnam) throw new Error('缺少公司名稱');

            const abd = raw['國籍'] === '境外' ? 1 : 0;

            const payload = {
                vdR_TYP: 'VD',
                caT_SNO: category.sno,
                fnam,
                bsn: raw['統一編號'] ? String(raw['統一編號']) : '',
                abd,
                abD_TYP: null,
                profitType: raw['營利類型'] === '非營利事業' ? 2 : 1,
                ara: null,
                cty: null,
                adr: raw['地址'] || '',
                adR_CODE: raw['郵遞區號'] ? String(raw['郵遞區號']) : '',
                note: raw['備註'] || '',
                onf: 1,
                oS_GRP: false,
                oS_GRP_FILE: '',
                vendorContacts: this.parseContacts(raw),
                vendorBanks: this.parseBanks(raw),
            };

            if (abd === 1) {
                const ABD_TYP_MAP = { 民間: 2, 公營事業: 1, 政府: 0 };
                payload.abD_TYP = ABD_TYP_MAP[raw['境外類型']] ?? 2;
                payload.ara = raw['縣市／國家'] || '';
            } else {
                payload.cty = raw['縣市／國家'] || '';
            }

            if (raw['系統編號']) payload.voo = String(raw['系統編號']);

            return payload;
        },

        // ── 執行匯入 ─────────────────────────────────────────────
        async onImport() {
            this.importStatus = 'importing';
            const total = this.parsedRows.length;
            let done = 0;

            for (const row of this.parsedRows) {
                if (this.isStopping) break;
                row.importStatus = 'importing';
                done++;
                this.progress = Math.round((done / total) * 100);

                try {
                    const payload = this.buildPayload(row._raw);
                    // eslint-disable-next-line no-await-in-loop
                    await this.$api_silent({
                        url: 'Outsourcing/UpsertOutsourcingDetail',
                        data: payload,
                        method: 'POST',
                    });
                    row.importStatus = '已新增';
                } catch (err) {
                    // Axios 錯誤有 response；buildPayload 拋的是 Error
                    if (err.response) {
                        row.importStatus = err.response.status === 400
                            ? '缺少欄位'
                            : (err.response.data || '伺服器錯誤');
                    } else {
                        row.importStatus = err.message || err.msg || '未知錯誤';
                    }
                }
            }

            this.importStatus = 'complete';
        },

        // ── 狀態顯示 ─────────────────────────────────────────────
        statusLabel(s) {
            if (s === 'init') return '---';
            if (s === 'importing') return '匯入中...';
            return s;
        },
        statusClass(s) {
            if (s === '已新增') return 'status-ok';
            if (s === 'init' || s === 'importing') return '';
            return 'status-err';
        },
    },
};
</script>

<style lang="scss" scoped>
.upload-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    .upload-btn {
        width: 260px;
    }
    .hint {
        font-size: 13px;
        color: #888;
        text-align: center;
    }
}
.status-ok {
    color: #44bf00;
    &::before { content: 'Ｏ'; }
}
.status-err {
    color: red;
    &::before { content: 'Ｘ '; }
}
</style>

<style lang="scss">
.outsourcing-vendor-import-dialog.ah-detail-dialog {
    .detail-row {
        padding: 40px;
    }
}
</style>
